import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { API, CONTACT } from "../config/site";

const INTERESTS = ["Live in it", "Second home", "Investment", "Still deciding"];
const TIMELINES = ["0-6 months", "6-12 months", "1-2 years", "Just watching"];
const BUDGETS = ["Up to $1M", "$1M – $1.4M", "$1.4M – $2M", "$2M – $3.5M", "$3.5M+", "Prefer not to say"];

const inputCls =
  "w-full bg-transparent border-b border-ivory/30 focus:border-ivory outline-none py-3 text-ivory placeholder:text-ivory/40 text-base transition-colors duration-300 rounded-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-ivory/60">{label}</span>
    {children}
  </label>
);

export const AccessGate = ({ onUnlock }) => {
  const [step, setStep] = useState(localStorage.getItem("gate_code_ok") === "1" ? 2 : 1);
  const [code, setCode] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", timeline: "", price_range: "", tour: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/gate/verify`, { code });
      localStorage.setItem("gate_code_ok", "1");
      localStorage.setItem("gate_code_value", code.trim());
      setStep(2);
    } catch {
      setError("That code isn't right — check the message Marlene sent you, or text her at 808-989-6741.");
    } finally {
      setLoading(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, { ...form, access_code: localStorage.getItem("gate_code_value") || "" });
      localStorage.setItem("gate_done", "1");
      onUnlock();
    } catch {
      setError("Something went wrong — please try again or text Marlene at 808-989-6741.");
      setLoading(false);
    }
  };

  return (
    <div className="grain fixed inset-0 z-[100] bg-teal overflow-y-auto" data-testid="access-gate">
      <div className="min-h-full flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form
                key="code"
                onSubmit={verifyCode}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid="gate-code-step"
              >
                <Lock size={22} strokeWidth={1.5} className="text-ivory/70" />
                <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-ivory/60 mt-8">Private page</p>
                <h1 className="font-serif text-3xl md:text-4xl font-light text-ivory mt-4 leading-snug">
                  This page is shared personally by Marlene Alesna.
                </h1>
                <p className="text-sm text-ivory/70 leading-relaxed mt-4">
                  Enter your access code to continue.
                </p>
                <div className="mt-10">
                  <Field label="Access code" testid="gate-code-field">
                    <input
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter your code"
                      autoFocus
                      className={inputCls}
                      data-testid="gate-code-input"
                    />
                  </Field>
                </div>
                {error && <p className="text-sm text-sand mt-4" data-testid="gate-code-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative overflow-hidden mt-10 w-full sm:w-auto bg-ivory text-teal px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold disabled:opacity-70"
                  data-testid="gate-code-submit"
                >
                  <span className="absolute inset-0 bg-sand translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                    {loading ? "Checking..." : "Continue"}
                    {!loading && <ArrowRight size={14} />}
                  </span>
                </button>
                <p className="text-xs text-ivory/50 mt-10">
                  Don&rsquo;t have a code? Text Marlene at{" "}
                  <a href={CONTACT.smsHref} className="text-ivory/80 underline underline-offset-4" data-testid="gate-text-marlene-link">{CONTACT.phoneDisplay}</a>.
                </p>
              </motion.form>
            ) : (
              <motion.form
                key="register"
                onSubmit={register}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid="gate-register-step"
              >
                <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-ivory/60">Almost there</p>
                <h1 className="font-serif text-3xl md:text-4xl font-light text-ivory mt-4 leading-snug">
                  Aloha — introduce yourself.
                </h1>
                <p className="text-sm text-ivory/70 leading-relaxed mt-4">
                  A few quick details so Marlene knows who&rsquo;s viewing and can follow up personally. This also adds you to her buyer list.
                </p>
                <p className="text-sm text-ivory/85 leading-relaxed mt-6" data-testid="gate-tour-intro">
                  Tell me a bit about what you&rsquo;re looking for and I&rsquo;ll set up your showroom tour.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 mt-10">
                  <Field label="Name *" testid="gate-field-name">
                    <input required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="gate-name-input" />
                  </Field>
                  <Field label="Email *" testid="gate-field-email">
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="gate-email-input" />
                  </Field>
                  <Field label="Phone *" testid="gate-field-phone">
                    <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="Best number for a text" className={inputCls} data-testid="gate-phone-input" />
                  </Field>
                  <Field label="I'm interested in *" testid="gate-field-interest">
                    <select required value={form.interest} onChange={set("interest")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="gate-interest-select">
                      <option value="" disabled className="text-charcoal">Select one</option>
                      {INTERESTS.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Timeline *" testid="gate-field-timeline">
                    <select required value={form.timeline} onChange={set("timeline")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="gate-timeline-select">
                      <option value="" disabled className="text-charcoal">Select one</option>
                      {TIMELINES.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                    </select>
                  </Field>
                  <Field label="My budget (optional)" testid="gate-field-budget">
                    <select value={form.price_range} onChange={set("price_range")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="gate-budget-select">
                      <option value="" className="text-charcoal">Prefer to discuss later</option>
                      {BUDGETS.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                    </select>
                  </Field>
                </div>
                <label className="flex items-start sm:items-center gap-3 cursor-pointer mt-8" data-testid="gate-field-tour">
                  <input
                    type="checkbox"
                    checked={form.tour}
                    onChange={(e) => setForm({ ...form, tour: e.target.checked })}
                    className="mt-0.5 sm:mt-0 w-4 h-4 accent-[#FAF9F6] cursor-pointer"
                    data-testid="gate-tour-checkbox"
                  />
                  <span className="text-sm text-ivory/85 leading-snug">I&rsquo;d like to tour &mdash; please contact me about scheduling</span>
                </label>
                {error && <p className="text-sm text-sand mt-4" data-testid="gate-register-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative overflow-hidden mt-10 w-full sm:w-auto bg-ivory text-teal px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold disabled:opacity-70"
                  data-testid="gate-register-submit"
                >
                  <span className="absolute inset-0 bg-sand translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                    {loading ? "One moment..." : "Enter the site"}
                    {!loading && <ArrowRight size={14} />}
                  </span>
                </button>
                <p className="text-xs text-ivory/50 mt-8">No spam, no obligation. Your information stays between you and Marlene.</p>
              </motion.form>
            )}
          </AnimatePresence>
          <p className="text-[11px] text-ivory/40 mt-14 leading-relaxed" data-testid="gate-disclaimer">
            Marlene Alesna &middot; REALTOR&reg; &middot; eXp Realty &middot; Independent buyer&rsquo;s agent &mdash; not the developer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessGate;
